import { Link } from "react-router-dom";
import Hero from "@/components/feature/Hero";
import { services } from "@/mocks/services";
import { testimonials } from "@/mocks/testimonials";

export default function Home() {
  return (
    <>
      <Hero />

      {/* Services Preview */}
      <section className="py-20 md:py-28 bg-background-50">
        <div className="px-4 md:px-6 lg:px-10">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-accent-500 text-xs font-semibold tracking-[0.25em] uppercase mb-4 block">
                What We Offer
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-6">
                Our <span className="text-primary-600">Security Services</span>
              </h2>
              <p className="text-foreground-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
                Comprehensive security solutions tailored to meet your specific protection needs across all industries.
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {services.slice(0, 6).map((service) => (
                <div
                  key={service.id}
                  className="group bg-background-100 rounded-xl p-6 md:p-8 border border-background-200/70 hover:border-primary-300 transition-all duration-500 hover:-translate-y-1"
                >
                  <div className="w-14 h-14 flex items-center justify-center bg-primary-100 rounded-xl mb-6 group-hover:bg-primary-500 transition-colors duration-500">
                    <i className={`${service.icon} text-primary-600 text-2xl group-hover:text-background-50 transition-colors duration-500`} />
                  </div>
                  <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-foreground-600 text-sm leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <Link
                    to="/services"
                    className="inline-flex items-center gap-2 text-primary-600 font-semibold text-sm hover:text-accent-500 transition-colors duration-300"
                  >
                    Learn More
                    <i className="ri-arrow-right-line group-hover:translate-x-1 transition-transform duration-300" />
                  </Link>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/services"
                className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold px-8 py-4 rounded-md transition-all duration-300 whitespace-nowrap text-sm"
              >
                View All Services
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Preview */}
      <section className="py-20 md:py-28 bg-background-100">
        <div className="px-4 md:px-6 lg:px-10">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <span className="text-accent-500 text-xs font-semibold tracking-[0.25em] uppercase mb-4 block">
                Client Feedback
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-6">
                Trusted by <span className="text-primary-600">Clients Across California</span>
              </h2>
              <p className="text-foreground-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
                Real feedback from businesses and individuals we protect every day.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
              {testimonials.slice(0, 3).map((testimonial) => (
                <div
                  key={testimonial.id}
                  className="bg-background-50 rounded-xl p-8 border border-background-200/70 flex flex-col"
                >
                  <div className="flex items-center gap-1 mb-6">
                    {Array.from({ length: testimonial.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm" />
                    ))}
                  </div>
                  <p className="text-foreground-700 text-sm leading-relaxed italic flex-grow mb-6">
                    &ldquo;{testimonial.quote}&rdquo;
                  </p>
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="font-heading text-primary-700 font-bold text-sm">
                        {testimonial.name.split(" ").map((n) => n[0]).join("")}
                      </span>
                    </div>
                    <div>
                      <div className="font-semibold text-foreground-950 text-sm">
                        {testimonial.name}
                      </div>
                      <div className="text-foreground-500 text-xs">
                        {testimonial.title}, {testimonial.company}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/testimonials"
                className="inline-flex items-center gap-2 border border-primary-500 hover:bg-primary-500/10 text-primary-700 font-semibold px-8 py-4 rounded-md transition-all duration-300 whitespace-nowrap text-sm"
              >
                See More Reviews
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-16 md:py-20 bg-primary-700">
        <div className="px-4 md:px-6 lg:px-10 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-background-50 mb-4">
              Ready to Secure Your Business?
            </h2>
            <p className="text-background-50/70 text-base mb-8 max-w-xl mx-auto leading-relaxed">
              Get a free, no-obligation security assessment from our expert team today.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-semibold px-8 py-4 rounded-md transition-all duration-300 whitespace-nowrap text-sm"
            >
              Request a Quote
              <i className="ri-arrow-right-line" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}